(() => {
  'use strict';

  if (window.CampBoksAnalytics) return;
  const node = document.getElementById('cb-posthog-config');
  if (!node) return;
  const config = node.dataset;
  const preview = config.themeRole !== 'main';
  const key = config.projectKey || '';
  const enabled = /^phc_[a-zA-Z0-9]+$/.test(key) && (!preview || config.allowPreview === 'true');
  const blockedPage = /\/(account|checkouts?|orders|password)(\/|$)/i;
  const sensitive = 'input, textarea, select, [contenteditable], .ph-no-capture, [data-ph-sensitive]';
  const eventFields = {
    cta_clicked: ['cta_id', 'placement', 'target_path'],
    cta_viewed: ['cta_id', 'placement'],
    cta_peek_shown: ['reason', 'placement'],
    cta_peek_closed: ['why'],
    booking_started: ['provider', 'placement'],
    booking_completed: ['provider', 'daypart'],
    phone_clicked: ['placement', 'action'],
    lead_submitted: ['form_id', 'channel'],
    chat_opened: [],
    chat_message_sent: [],
    chat_closed: ['duration_s', 'messages'],
    chat_callback_offered: ['reason'],
    chat_callback_clicked: ['reason'],
    chat_contact_opened: ['reason'],
    chat_error: ['error_code'],
    configuration_started: ['model'],
    configuration_updated: ['model', 'step'],
    video_opened: ['video_id'],
    video_started: ['video_id', 'placement'],
    cart_opened: [],
    checkout_clicked: ['placement'],
  };
  const numericFields = new Set(['duration_s', 'messages']);
  // Existing dataLayer names of the cb components -> PostHog contract. Only listed fields pass;
  // slot_start (callback time) is dropped on purpose. cb_call_click is covered by the tel: listener.
  const aliases = {
    cb_ki_chat_open: () => ['chat_opened'],
    cb_ki_chat_message: () => ['chat_message_sent'],
    cb_ki_chat_close: (p) => ['chat_closed', p],
    cb_ki_chat_rueckruf_angeboten: (p) => ['chat_callback_offered', p],
    cb_ki_chat_termin_click: (p) => ['chat_callback_clicked', p],
    cb_ki_chat_contact_open: (p) => ['chat_contact_opened', p],
    cb_ki_chat_contact_submit: (p) => ['lead_submitted', { form_id: 'ki_chat_contact', channel: p.channel }],
    cb_ki_chat_error: (p) => ['chat_error', p],
    cb_ki_chat_contact_error: (p) => ['chat_error', p],
    cb_rueckruf_start: () => ['booking_started', { provider: 'rueckruf' }],
    cb_rueckruf_booked: (p) => ['booking_completed', { provider: 'rueckruf', daypart: p.daypart }],
    cb_beratung_link_intercept: () => ['cta_clicked', { cta_id: 'beratungstermin', placement: 'rueckruf_panel' }],
    cb_video_open: (p) => ['video_opened', { video_id: p.cb_video_id }],
  };
  // Order matters: the callback form lives inside the sticky panel or the chat.
  const phonePlacements = [['cb-rueckruf', 'rueckruf'], ['cb-ki-chat', 'ki_chat'], ['cb-sf-v6-sticky', 'sticky'],
    ['.cb-kauf-bar', 'kauf_bar'], ['footer, .footer, #shopify-section-footer-group', 'footer'], ['header, #header-group', 'header']];
  let sdk;
  let initialized = false;
  let loading = false;
  let granted = false;
  let state = enabled ? 'waiting_for_consent' : 'disabled';
  let seen = new WeakSet();
  let observer;
  let flagReady = false;
  let consentSince = 0;
  let consentEpoch = 0;
  let transportGuarded = false;
  let revoked = false;
  let ctaScan = 0;
  const flagListeners = new Set();
  const token = (value) => typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,100}$/.test(value) ? value : undefined;

  function safeURL(value, originOnly = false) {
    try {
      const url = new URL(value, window.location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';
      const path = blockedPage.test(url.pathname) ? '/[private]' : url.pathname;
      return url.origin + (originOnly ? '/' : path.replace(/[^/]*(@|%40)[^/]*/gi, '[redacted]'));
    } catch (_) { return ''; }
  }

  // Links may carry utm_*/gclid from the UTM snippet; analytics only needs the path.
  function safeLink(value) {
    return typeof value === 'string' && !/^(tel|mailto):|^#/i.test(value) ? safeURL(value) : value;
  }

  function consent() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return enabled && !window.Shopify?.designMode && !blockedPage.test(window.location.pathname)
        && privacy?.analyticsProcessingAllowed() === true
        && privacy.currentVisitorConsent().analytics === 'yes';
    } catch (_) { return false; }
  }

  function context() {
    return {
      integration: 'campboks_native_v1',
      environment: preview ? 'preview' : 'production',
      theme_id: String(config.themeId),
      page_type: token(config.pageType) || 'unknown',
      // Seitensprache, Land und Shopify-Markt der Seite (aus Liquid), z. B. fr / AT / eu.
      page_language: token(config.locale) || 'unknown',
      shop_country: token(config.country) || 'unknown',
      shop_market: token(config.market) || 'unknown',
      $current_url: safeURL(window.location.href),
    };
  }

  function beforeSend(event) {
    if (!granted || !consent()) return null;
    const props = event.properties || {};
    for (const name of Object.keys(props)) {
      // ph_keyword: search terms the SDK parses from the referrer.
      if (/email|phone|address|form_body|\$set|\$initial|gclid|fbclid|msclkid|utm_term|ph_keyword/i.test(name)) delete props[name];
      // Sentinels such as '$direct' are not URLs and must stay untouched.
      else if (/url|href|referrer/i.test(name) && typeof props[name] === 'string' && /^https?:/i.test(props[name])) props[name] = safeURL(props[name], /referrer/i.test(name));
    }
    // Autocapture copies link targets into the element chain, including gclid and utm_term.
    if (Array.isArray(props.$elements)) {
      props.$elements = props.$elements.map((element) => {
        const clean = { ...element };
        for (const name of ['attr__href', 'href']) if (clean[name]) clean[name] = safeLink(clean[name]);
        return clean;
      });
    }
    if (typeof props.$elements_chain === 'string') {
      props.$elements_chain = props.$elements_chain.replace(/((?:attr__)?href=")((?:[^"\\]|\\.)*)"/g, (_, prefix, value) => prefix + safeLink(value) + '"');
    }
    event.properties = { ...props, ...context() };
    return event;
  }

  // Replays show page text and layout; chat, callback, forms and the account menu stay masked.
  const replayMasked = 'cb-ki-chat, cb-rueckruf, form, .account-actions, .ph-no-capture, [data-ph-sensitive]';

  function replayAttribute(name, value, element) {
    if (name === 'href' || name === 'action') return safeLink(value);
    if (/^(value|placeholder|title|alt|aria-|data-)/i.test(name) && element?.closest?.(replayMasked)) return '*'.repeat(value.length);
    return value;
  }

  function options() {
    return {
      api_host: 'https://r.campboks.com', // Reverse Proxy (PostHog-managed, seit 28.09.2026)
      ui_host: 'https://eu.posthog.com',
      defaults: '2026-01-30',
      strict_script_versioning: true,
      disable_external_dependency_loading: true,
      persistence: 'localStorage',
      persistence_name: 'cb_posthog',
      cross_subdomain_cookie: false,
      secure_cookie: true,
      person_profiles: 'never',
      ip: false,
      // Only the referring domain is kept (beforeSend cuts $referrer to its origin); it separates
      // Google organic, social and referrals from direct visits.
      save_referrer: true,
      save_campaign_params: false,
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: { dom_event_allowlist: ['click'], element_allowlist: ['a', 'button'], capture_copied_text: false },
      mask_all_text: true,
      mask_all_element_attributes: true,
      capture_heatmaps: config.heatmaps === 'true',
      capture_dead_clicks: false,
      capture_performance: false,
      capture_exceptions: false,
      enable_recording_console_log: false,
      disable_surveys: true,
      disable_conversations: true,
      disable_product_tours: true,
      disable_web_experiments: true,
      advanced_disable_feature_flags: config.experiments !== 'true',
      disable_session_recording: config.replay !== 'true',
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      request_batching: false,
      get_current_url: () => safeURL(window.location.href),
      before_send: beforeSend,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: replayMasked,
        maskAttributeFn: replayAttribute,
        blockSelector: '[data-ph-sensitive], .ph-no-capture, iframe, [contenteditable]',
        recordHeaders: false,
        recordBody: false,
        recordPerformance: false,
        captureCanvas: { recordCanvas: false },
        captureJsonLd: false,
        recordCrossOriginIframes: false,
        maskCapturedNetworkRequestFn: (request) => ({ ...request, name: safeURL(request.name) }),
      },
    };
  }

  function publishContext() {
    if (!granted || !consent() || !sdk || config.checkoutBridge !== 'true') return;
    try {
      const payload = {
        distinct_id: sdk.get_distinct_id(), session_id: sdk.get_session_id(),
        environment: context().environment, theme_id: String(config.themeId),
        updated_at: Date.now(), consent_since: consentSince,
      };
      window.localStorage.setItem('cb_posthog_checkout_context', JSON.stringify(payload));
      window.Shopify?.analytics?.publish('cb_posthog_context', payload);
    } catch (_) { /* Analytics must never interrupt navigation or checkout. */ }
  }

  function capture(name, properties = {}) {
    if (!Object.hasOwn(eventFields, name) || state !== 'ready' || !consent()) return false;
    if (!properties || typeof properties !== 'object') properties = {};
    const clean = {};
    for (const field of eventFields[name]) {
      const value = properties[field];
      if (field === 'target_path') {
        if (typeof value !== 'string' || !value) continue;
        try {
          const url = new URL(value, window.location.origin);
          if (url.origin === window.location.origin) clean[field] = new URL(safeURL(url.href)).pathname;
        } catch (_) { /* Invalid destinations are omitted. */ }
      } else if (numericFields.has(field)) {
        if (Number.isFinite(value) && value >= 0) clean[field] = Math.min(Math.round(value), 86400);
      } else if (token(value)) clean[field] = value;
    }
    // pagehide (e.g. leaving with the chat open): a normal request is cancelled by the unload.
    const unloading = document.visibilityState === 'hidden';
    sdk.capture(name, { ...clean, ...context() }, unloading ? { transport: 'sendBeacon' } : undefined);
    publishContext();
    return true;
  }

  function observeCTAs() {
    if (!observer || state !== 'ready') return;
    document.querySelectorAll('[data-ph-cta]').forEach((element) => {
      if (!seen.has(element) && !element.closest(sensitive)) observer.observe(element);
    });
  }

  function activate() {
    if (!consent() || !sdk) return;
    consentSince = Date.now();
    granted = true;
    revoked = false;
    sdk.set_config({ ...options(), disable_persistence: false, advanced_disable_flags: false });
    sdk.opt_in_capturing({ captureEventName: false });
    state = 'ready';
    const campaign = {};
    const search = new URLSearchParams(window.location.search);
    for (const name of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
      const value = search.get(name);
      if (token(value)) campaign[name] = value;
    }
    // Only whether the visit came from an ad click, never the click ID itself. Shopping ads and
    // free listings share utm_medium=product_sync and differ only by this.
    const adClick = [['gclid', 'google'], ['gbraid', 'google'], ['wbraid', 'google'], ['fbclid', 'meta'], ['msclkid', 'microsoft']]
      .find(([name]) => search.get(name));
    if (adClick) campaign.ad_click = adClick[1];
    // Campaign values belong to this session only; a later direct visit must not inherit them.
    sdk.register(context());
    sdk.register_for_session(campaign);
    sdk.capture('$pageview', context());
    if (config.experiments === 'true') sdk.reloadFeatureFlags();
    observeCTAs();
    publishContext();
    document.dispatchEvent(new CustomEvent('cb:analytics:ready'));
  }

  function revoke() {
    revoked = true;
    granted = false;
    consentEpoch += 1;
    flagReady = false;
    seen = new WeakSet();
    observer?.disconnect();
    if (sdk && initialized) {
      sdk.opt_out_capturing();
      sdk.stopSessionRecording();
      sdk.set_config({ disable_persistence: true, autocapture: false, capture_heatmaps: false,
        capture_pageleave: false, disable_session_recording: true, advanced_disable_flags: true,
        disable_external_dependency_loading: true });
      sdk.reset(true);
    }
    try { window.localStorage.removeItem('cb_posthog_checkout_context'); } catch (_) { /* Storage can be unavailable. */ }
    state = enabled ? 'waiting_for_consent' : 'disabled';
    document.dispatchEvent(new CustomEvent('cb:analytics:stopped'));
  }

  function syncConsent() {
    if (!consent()) { if (granted || (sdk && !revoked)) revoke(); return; }
    if (state === 'ready' || loading) return;
    if (sdk) { if (initialized) activate(); else initialize(); return; }
    if (window.posthog) { state = 'existing_sdk'; return; }
    loading = true;
    state = 'loading';
    const script = document.createElement('script');
    script.async = true;
    script.src = config.sdkUrl;
    script.onload = () => {
      loading = false;
      sdk = window.posthog;
      if (!consent()) { state = 'waiting_for_consent'; return; }
      initialize();
    };
    script.onerror = () => { loading = false; state = 'sdk_error'; script.remove(); };
    document.head.appendChild(script);
  }

  function initialize() {
    try {
      if (!transportGuarded) {
        if (typeof sdk._send_request !== 'function' || typeof sdk._send_retriable_request !== 'function') {
          state = 'unsupported_sdk';
          return;
        }
        // Version-pinned adapter: SDK retries bypass before_send. Drop revoked
        // requests, including retries from an earlier consent period.
        const send = sdk._send_request.bind(sdk);
        const retriable = sdk._send_retriable_request.bind(sdk);
        sdk._send_request = (request) => {
          if (!granted || !consent() || (request.cbConsentEpoch !== undefined && request.cbConsentEpoch !== consentEpoch)) {
            request.callback?.({ statusCode: 400 });
            return;
          }
          return send(request);
        };
        sdk._send_retriable_request = (request) => retriable({ ...request, cbConsentEpoch: consentEpoch });
        transportGuarded = true;
      }
      granted = true;
      sdk.init(key, { ...options(), loaded: () => {
        initialized = true;
        sdk.onSessionId(publishContext);
        sdk.onFeatureFlags(() => {
          if (!granted || !consent()) return;
          flagReady = true;
          flagListeners.forEach((listener) => listener());
        });
        activate();
      } });
    } catch (_) { state = 'sdk_error'; }
  }

  window.CampBoksAnalytics = Object.freeze({
    capture,
    status: () => state,
    refreshConsent: syncConsent,
    onVariant(key, callback) {
      if (!token(key) || typeof callback !== 'function') return () => {};
      const notify = () => {
        if (state === 'ready' && flagReady && config.experiments === 'true') callback(sdk.getFeatureFlag(key, { send_event: false }));
      };
      flagListeners.add(notify);
      notify();
      return () => flagListeners.delete(notify);
    },
    expose(key) {
      if (state !== 'ready' || !consent() || !flagReady || config.experiments !== 'true' || !token(key)) return undefined;
      return sdk.getFeatureFlag(key);
    },
  });

  if (!enabled || window.Shopify?.designMode) return;
  document.addEventListener('visitorConsentCollected', syncConsent);
  window.addEventListener('pageshow', syncConsent);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) syncConsent(); });
  window.addEventListener('storage', syncConsent);
  document.addEventListener('cb:analytics', (event) => {
    const detail = event.detail;
    if (!detail || typeof detail.event !== 'string') return;
    const props = detail.properties && typeof detail.properties === 'object' ? detail.properties : {};
    if (Object.hasOwn(aliases, detail.event)) capture(...aliases[detail.event](props));
    else capture(detail.event, props);
  });
  // Adding to cart comes from the checkout pixel (docs/posthog/customer-events.js, with value).
  // The theme only adds what the pixel cannot see; dialog:open does not bubble, hence the capture phase.
  document.addEventListener('dialog:open', (event) => {
    if (event.target?.matches?.('cart-drawer-component')) capture('cart_opened');
  }, true);
  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.('button[name="checkout"], a[href^="/checkout"]');
    if (checkout) capture('checkout_clicked', { placement: checkout.closest('cart-drawer-component') ? 'drawer' : 'cart_page' });
    const phone = event.target.closest?.('a[href^="tel:"]');
    if (phone) {
      const match = phonePlacements.find(([selector]) => phone.closest(selector));
      // The desktop sticky button prevents the call and copies the number instead.
      capture('phone_clicked', { placement: token(phone.dataset.phPlacement) || (match ? match[1] : 'page'), action: event.defaultPrevented ? 'copy' : 'call' });
    }
    const element = event.target.closest?.('[data-ph-cta]');
    if (!element || element.closest(sensitive)) return;
    capture('cta_clicked', { cta_id: element.dataset.phCta, placement: element.dataset.phPlacement, target_path: element.getAttribute('href') });
  });
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || seen.has(entry.target)) continue;
        if (capture('cta_viewed', { cta_id: entry.target.dataset.phCta, placement: entry.target.dataset.phPlacement })) {
          seen.add(entry.target);
          observer.unobserve(entry.target);
        }
      }
    }, { threshold: 0.5 });
    document.addEventListener('shopify:section:load', observeCTAs);
    // Streaming chat answers mutate the DOM constantly; scan for new CTAs at most twice a second.
    new MutationObserver(() => {
      if (!ctaScan) ctaScan = window.setTimeout(() => { ctaScan = 0; observeCTAs(); }, 500);
    }).observe(document.body, { childList: true, subtree: true });
  }
  let attempts = 0;
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy) { syncConsent(); return; }
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) syncConsent();
        else state = 'privacy_unavailable';
      });
    } else if (++attempts < 40) window.setTimeout(loadPrivacy, 250);
    else state = 'privacy_unavailable';
  };
  loadPrivacy();
})();
